import { useState } from 'react';
import {
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  SkipForward,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Package,
  AlertTriangle,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { MigrationModule } from '@/types';

interface ModuleProgressCardProps {
  module: MigrationModule;
  moduleName: string;
  onRetry?: ((moduleKey: string) => void) | undefined;
  isRetrying?: boolean | undefined;
}

type OverallStatus =
  | 'pending'
  | 'migrating'
  | 'succeeded'
  | 'failed'
  | 'skipped'
  | 'provisioningFailed'
  | 'metadataUpdateFailed';

function getOverallStatus(module: MigrationModule): OverallStatus {
  if (module.migrateStatus === 'Skipped') return 'skipped';
  if (module.migrateStatus === 'Failed') return 'failed';
  if (module.migrateStatus === 'Succeeded' && module.amplifyProvisioningStatus === 'Failed')
    return 'provisioningFailed';
  if (
    module.migrateStatus === 'Succeeded' &&
    module.amplifyProvisioningStatus === 'Succeeded' &&
    module.amplifyMetadataUpdateStatus === 'Failed'
  )
    return 'metadataUpdateFailed';
  if (module.migrateStatus === 'Succeeded') return 'succeeded';
  if (module.migrateStatus === 'InProgress') return 'migrating';
  return 'pending';
}

const statusConfig: Record<OverallStatus, {
  icon: React.ElementType;
  label: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  iconClass: string;
}> = {
  pending: {
    icon: Clock,
    label: 'Pending',
    variant: 'secondary',
    iconClass: 'text-muted-foreground',
  },
  migrating: {
    icon: Loader2,
    label: 'Migrating',
    variant: 'default',
    iconClass: 'text-primary animate-spin',
  },
  succeeded: {
    icon: CheckCircle2,
    label: 'Completed',
    variant: 'outline',
    iconClass: 'text-success',
  },
  failed: {
    icon: XCircle,
    label: 'Failed',
    variant: 'destructive',
    iconClass: 'text-destructive',
  },
  skipped: {
    icon: SkipForward,
    label: 'Skipped',
    variant: 'secondary',
    iconClass: 'text-muted-foreground',
  },
  provisioningFailed: {
    icon: AlertTriangle,
    label: 'Provisioning Failed',
    variant: 'destructive',
    iconClass: 'text-destructive',
  },
  metadataUpdateFailed: {
    icon: AlertTriangle,
    label: 'Metadata Update Failed',
    variant: 'destructive',
    iconClass: 'text-destructive',
  },
};

export function ModuleProgressCard({
  module,
  moduleName,
  onRetry,
  isRetrying,
}: ModuleProgressCardProps) {
  const [expanded, setExpanded] = useState(false);
  const status = getOverallStatus(module);
  const config = statusConfig[status];
  const Icon = config.icon;

  const hasError = !!module.migrateError || !!module.amplifyMetadataUpdateError;
  const canRetry =
    (status === 'failed' || status === 'provisioningFailed' || status === 'metadataUpdateFailed') &&
    onRetry;

  // Async-polled module — surface the live poll attempt while migrating.
  const isAsyncInFlight =
    status === 'migrating' && !!module.migrationJobId && module.migrationJobPollAttempts > 0;

  const getProgress = () => {
    if (status === 'pending') return 0;
    if (status === 'migrating') return 50;
    if (status === 'succeeded' || status === 'skipped') return 100;
    if (status === 'failed') return 50;
    if (status === 'provisioningFailed') return 100;
    if (status === 'metadataUpdateFailed') return 100;
    return 0;
  };

  return (
    <Card
      className={cn(
        'transition-all',
        (status === 'failed' ||
          status === 'provisioningFailed' ||
          status === 'metadataUpdateFailed') && 'border-destructive/50',
        status === 'succeeded' && 'border-success/50',
        status === 'migrating' && 'border-primary/50'
      )}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Package className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base truncate">{moduleName}</CardTitle>
              <p className="text-xs text-muted-foreground truncate">{module.moduleKey}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
            <Badge variant={config.variant} className="gap-1 whitespace-nowrap">
              <Icon className={cn('h-3 w-3', config.iconClass)} />
              {config.label}
            </Badge>
            {module.amplifyProvisioningStatus !== 'NotApplicable' &&
              module.amplifyProvisioningStatus !== 'Failed' && (
                <Badge
                  variant={module.amplifyProvisioningStatus === 'Succeeded' ? 'outline' : 'secondary'}
                  className="gap-1 text-xs whitespace-nowrap"
                >
                  Amplify: {module.amplifyProvisioningStatus}
                </Badge>
              )}
            {module.amplifyMetadataUpdateStatus !== 'NotApplicable' &&
              module.amplifyMetadataUpdateStatus !== 'Failed' && (
                <Badge
                  variant={
                    module.amplifyMetadataUpdateStatus === 'Succeeded' ? 'outline' : 'secondary'
                  }
                  className="gap-1 text-xs whitespace-nowrap"
                >
                  Metadata: {module.amplifyMetadataUpdateStatus}
                </Badge>
              )}
            {hasError && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 shrink-0"
                onClick={() => setExpanded(!expanded)}
                aria-expanded={expanded}
                aria-label={expanded ? 'Collapse details' : 'Expand details'}
              >
                {expanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Progress bar */}
        <div className="space-y-1">
          <Progress value={getProgress()} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {status === 'migrating' && !isAsyncInFlight && 'Migrating data...'}
              {isAsyncInFlight && `Polling job (attempt ${module.migrationJobPollAttempts})...`}
              {status === 'pending' && 'Waiting to start'}
              {status === 'succeeded' && 'Migration complete'}
              {status === 'failed' && 'Migration failed'}
              {status === 'skipped' && 'Skipped'}
              {status === 'provisioningFailed' && 'Provisioning failed'}
              {status === 'metadataUpdateFailed' && 'Metadata update failed'}
            </span>
            <span>Source: {module.sourceId}</span>
          </div>
        </div>

        {/* Error details (expandable) */}
        {expanded && hasError && (
          <div className="rounded-md bg-destructive/10 p-3 text-sm space-y-2">
            {module.migrateError && (
              <div>
                <span className="font-medium text-destructive">Migration Error:</span>
                <p className="mt-1 text-destructive/90">{module.migrateError}</p>
              </div>
            )}
            {module.amplifyMetadataUpdateError && (
              <div>
                <span className="font-medium text-destructive">Metadata Update Error:</span>
                <p className="mt-1 text-destructive/90">{module.amplifyMetadataUpdateError}</p>
              </div>
            )}
          </div>
        )}

        {/* Retry button */}
        {canRetry && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onRetry(module.moduleKey)}
            disabled={isRetrying}
            className="w-full"
          >
            {isRetrying ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {status === 'provisioningFailed'
              ? 'Retry Provisioning'
              : status === 'metadataUpdateFailed'
                ? 'Retry Metadata Update'
                : 'Retry Module'}
          </Button>
        )}

        {/* Timestamps */}
        {module.migrateCompletedAt && (
          <div className="flex justify-end text-xs text-muted-foreground pt-2 border-t">
            <span>Migrated: {new Date(module.migrateCompletedAt).toLocaleTimeString()}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
